import type {
  CreateExpense,
  Expense,
  ExpenseFilters,
  ExpenseWithSplits,
  UpdateExpense,
} from "@home-finance-manager/shared/types/expenses"
import { BaseSDK } from "./base-sdk"

export class ExpensesSDK extends BaseSDK {
  /**
   * Get all expenses for an account with cursor-based pagination
   * @param filters - Optional filters for expenses
   * @param cursor - Optional cursor (expense ID) for pagination
   * @param limit - Number of records to return (default: 50)
   * @returns Object with expenses array and nextCursor
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getExpenses(
    filters?: ExpenseFilters,
    cursor?: number,
    limit: number = 50
  ): Promise<{ data: Expense[] | ExpenseWithSplits[]; nextCursor: number | null }> {
    const params = new URLSearchParams()
    if (filters?.categoryIds && filters.categoryIds.length > 0) {
      filters.categoryIds.forEach((id) => params.append("categoryId", String(id)))
    }
    if (filters?.recurringTypes && filters.recurringTypes.length > 0) {
      filters.recurringTypes.forEach((type) => params.append("recurring", type))
    }
    if (filters?.isVirtual !== undefined) params.append("isVirtual", String(filters.isVirtual))
    if (filters?.search) params.append("search", filters.search)
    if (filters?.includeSplits) params.append("includeSplits", "true")
    if (cursor) params.append("cursor", String(cursor))
    if (limit !== 50) params.append("limit", String(limit))

    const queryString = params.toString()
    const url = `${this.baseUrl}/expenses${queryString ? `?${queryString}` : ""}`

    const response = await this.fetchWithErrorHandling<{
      data: Expense[] | ExpenseWithSplits[]
      nextCursor: number | null
    }>(url, {
      headers: this.getAuthHeaders(),
    })

    // Handle both { data: T, nextCursor: N } and direct { data: { data: T, nextCursor: N } } responses
    const responseData = this.extractData<{
      data: Expense[] | ExpenseWithSplits[]
      nextCursor: number | null
    }>(response)

    return {
      data: responseData?.data || (responseData as any) || [],
      nextCursor: responseData?.nextCursor ?? (response as any).nextCursor ?? null,
    }
  }

  /**
   * Get a specific expense by ID
   * @param id - The expense ID
   * @returns The expense
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getExpense(id: number): Promise<Expense> {
    const response = await this.fetchWithErrorHandling<{ data: Expense }>(
      `${this.baseUrl}/expenses/${id}`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<Expense>(response)
  }

  /**
   * Get expense with partner splits
   * @param id - The expense ID
   * @returns The expense with splits
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getExpenseWithSplits(id: number): Promise<ExpenseWithSplits> {
    const response = await this.fetchWithErrorHandling<{ data: ExpenseWithSplits }>(
      `${this.baseUrl}/expenses/${id}/splits`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<ExpenseWithSplits>(response)
  }

  /**
   * Create a new expense
   * @param expenseData - The expense data
   * @returns The created expense
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async createExpense(expenseData: CreateExpense): Promise<Expense> {
    const response = await this.fetchWithErrorHandling<{ data: Expense }>(
      `${this.baseUrl}/expenses`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(expenseData),
      }
    )
    return this.extractData<Expense>(response)
  }

  /**
   * Bulk create expenses
   * @param expensesData - Array of expense data
   * @returns Result with success/failure counts and errors
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async bulkCreateExpenses(expensesData: CreateExpense[]): Promise<{
    success: number
    failed: number
    errors: Array<{ index: number; error: string }>
  }> {
    const response = await this.fetchWithErrorHandling<{
      data: {
        success: number
        failed: number
        errors: Array<{ index: number; error: string }>
      }
    }>(`${this.baseUrl}/expenses/bulk`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(expensesData),
    })
    return this.extractData<{
      success: number
      failed: number
      errors: Array<{ index: number; error: string }>
    }>(response)
  }

  /**
   * Update an expense
   * @param id - The expense ID
   * @param expenseData - The updated expense data
   * @returns The updated expense
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async updateExpense(id: number, expenseData: UpdateExpense): Promise<Expense> {
    const response = await this.fetchWithErrorHandling<{ data: Expense }>(
      `${this.baseUrl}/expenses/${id}`,
      {
        method: "PUT",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(expenseData),
      }
    )
    return this.extractData<Expense>(response)
  }

  /**
   * Delete an expense
   * @param id - The expense ID
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async deleteExpense(id: number): Promise<{ message: string }> {
    return this.fetchWithErrorHandling<{ message: string }>(`${this.baseUrl}/expenses/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    })
  }
}
